import { NavLink, Outlet, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { GlobalSearch } from './GlobalSearch';
import { MerchantNoticeBar } from '@/components/ui/MerchantNoticeMarquee';
import {
  LayoutDashboard, Package, Wallet, Truck, Settings, LogOut, ChevronLeft, Bell,
  AlertTriangle, IndianRupee, FileText, Receipt, Calculator, Warehouse, Menu,
  ChevronDown, Code, Smartphone, Boxes, ShoppingBag, LifeBuoy,
  FileCheck2, BookOpen, Inbox,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, API_BASE_URL } from '@/api/client';
import { AccountSwitcher } from './AccountSwitcher';
import { ChatbotWidget } from '@/components/ai-chatbot/ChatbotWidget';
import { SignedImage } from '@/components/ui/SignedImage';
import { WalletBalanceMenu } from '@/components/layout/WalletBalanceMenu';
import { SupportContactCard } from '@/components/merchant/SupportContactCard';
import { useAuth, needsOnboarding } from '@/hooks/useAuth';
import { ThemeToggle } from '@/theme/ThemeToggle';
import { tokenStore } from '@/api/client';

// Merchant-only navigation - filtered by serviceType (B2C / B2B / BOTH)
type NavItem = { label: string; to: string; icon: any; b2cOnly?: boolean; b2bOnly?: boolean; wmsOnly?: boolean };
const MERCHANT_NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',         to: '/dashboard',          icon: <LayoutDashboard size={18} /> },
  { label: 'Orders B2C',        to: '/orders-b2c',         icon: <Package size={18} />,  b2cOnly: true },
  { label: 'Orders B2B',        to: '/orders-b2b',         icon: <Package size={18} />,  b2bOnly: true },
  // What the merchant's own system has pushed to our API and what became of each drop. Shown to
  // every merchant, not gated on serviceType — a bucket entry has no B2C/B2B split until we route it.
  { label: 'Orders Bucket',     to: '/orders-bucket',      icon: <Inbox size={18} /> },
  { label: 'NDR',               to: '/ndr',                icon: <AlertTriangle size={18} /> },
  { label: 'Remittance',        to: '/remittance',         icon: <IndianRupee size={18} /> },
  { label: 'Support Tickets',   to: '/tickets',            icon: <LifeBuoy size={18} /> },
  { label: 'Invoices',          to: '/invoices',           icon: <FileText size={18} /> },
  { label: 'Credit Notes',      to: '/credit-notes',       icon: <Receipt size={18} /> },
  { label: 'Debit Notes',       to: '/debit-notes',        icon: <Receipt size={18} /> },
  { label: 'Freight',           to: '/freight',            icon: <Calculator size={18} /> },
  { label: 'Price Calculator',  to: '/price-calculator',   icon: <Calculator size={18} /> },
  { label: 'Pickup Locations',  to: '/warehouses',         icon: <Warehouse size={18} /> },
  { label: 'WMS',               to: '/wms',                icon: <Boxes size={18} />, wmsOnly: true },
  { label: 'Sales Channels',    to: '/channels',           icon: <ShoppingBag size={18} /> },
  { label: 'Developer API',     to: '/api',                icon: <Code size={18} /> },
  { label: 'Wallet',            to: '/wallet',             icon: <Wallet size={18} /> },
  // Merchants download PODs one shipment at a time — the bulk ZIP is internal-staff only, so this
  // is deliberately not labelled "Bulk".
  { label: 'POD Download',      to: '/pod',                icon: <FileCheck2 size={18} /> },
  { label: 'Settings',          to: '/settings',           icon: <Settings size={18} /> },
  { label: 'Help',              to: '/help',               icon: <BookOpen size={18} /> },
];

function filterMerchantNav(serviceType?: 'B2C' | 'B2B' | 'BOTH', wmsEnabled?: boolean, shopifyAppInstall?: boolean): NavItem[] {
  const st = serviceType ?? 'BOTH';
  return MERCHANT_NAV_ITEMS.filter((it) => {
    if (it.b2cOnly && st === 'B2B') return false;
    if (it.b2bOnly && st === 'B2C') return false;
    if (it.wmsOnly && !wmsEnabled) return false;
    // Shopify App Store requirement 1.2.1 — our app is listed as free, so an account that arrived
    // through the App Store is shown no way to pay us anywhere in the product. The wallet is the only
    // payment surface, so it is the only thing removed; everything else about the app is unchanged.
    // The server refuses the recharge routes for these accounts too — this is only the visible half.
    if (shopifyAppInstall && it.to === '/wallet') return false;
    return true;
  });
}

export function MerchantLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  const { data: meProfile } = useQuery({
    queryKey: ['merchant', 'me', 'profile'],
    queryFn: async () => { try { return (await api.get('/merchants/me/profile')).data as any; } catch { return null; } },
    staleTime: 5 * 60 * 1000,
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!tokenStore.get()) return <Navigate to="/login" replace />;
  // Guard: only merchant kind allowed in merchant layout
  if (user && user.kind !== 'merchant') return <Navigate to="/login" replace />;
  // A self-registered merchant has to finish Seller Onboarding before the portal opens. This is the
  // friendly half only — the API refuses these sessions on every non-onboarding route regardless, so
  // typing a URL past this redirect gets a 403 rather than data.
  if (needsOnboarding(user)) return <Navigate to="/onboarding" replace />;
  const serviceType = (user?.kind === 'merchant' ? user.serviceType : undefined) as 'B2C' | 'B2B' | 'BOTH' | undefined;
  const isShopifyFreeApp = !!(meProfile as any)?.shopifyAppInstall;
  const merchantNav = filterMerchantNav(serviceType, !!(meProfile as any)?.wmsEnabled, isShopifyFreeApp);
  const { data: ticketStats } = useQuery<any>({ queryKey: ['ticket-stats-nav'], queryFn: async () => (await api.get('/tickets/stats')).data, refetchInterval: 60000, staleTime: 30000 });

  const displayName = user?.kind === 'merchant' ? user.companyName : user?.email;
  const impersonated = typeof window !== 'undefined' && window.sessionStorage.getItem('impersonated') === '1';

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside data-mobile-open={mobileNavOpen}
        className={`${collapsed ? 'w-16' : 'w-60'} bg-white border-r border-slate-200 flex flex-col transition-all shrink-0`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200">
          {!collapsed && (
            <div className="flex items-center gap-2 overflow-hidden">
              {/* logoUrl holds an R2 key now, not a URL — a raw <img src> can't render it. */}
              <SignedImage src={meProfile?.logoUrl} alt="logo" style={{ height: 28, maxWidth: 36, objectFit: 'contain' }}
                fallback={<div className="bg-primary text-white p-1.5 rounded-md"><Truck size={16} /></div>} />
              <span className="font-bold text-slate-900 truncate">
                {meProfile?.companyName ?? user?.companyName ?? 'Merchant'}
              </span>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="text-slate-400 hover:text-slate-600">
            <ChevronLeft size={18} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {merchantNav.map((item) => {
            const badge = item.to === '/tickets' ? (ticketStats?.active ?? 0) : 0;
            return (
              <NavLink key={item.to} to={item.to}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? 'bg-primary-50 text-primary' : 'text-slate-600 hover:bg-slate-100'}`}>
                {item.icon}
                {!collapsed && <span className="flex-1">{item.label}</span>}
                {!collapsed && badge > 0 && <span className="ml-auto bg-primary text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center leading-4">{badge}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-slate-200 p-2">
          <button onClick={() => logout()} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            <LogOut size={18} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileNavOpen(false)} />
      )}
      <div className="flex-1 flex flex-col min-w-0">
        {impersonated && (
          <div className="bg-amber-100 text-amber-900 text-xs px-4 py-1.5 text-center border-b border-amber-300">
            Admin impersonation session - actions are recorded against your admin account
          </div>
        )}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-3 md:px-6 gap-2">
          <button onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 text-slate-600">
            <Menu size={20} />
          </button>
          {/* The same search the admin portal uses. Safe to expose because tenancy is enforced in the
              API, not in this component — see the note on GlobalSearch. */}
          <div className="hidden md:flex flex-1 items-center min-w-0">
            <GlobalSearch placeholder="Search your AWB / LR, order no, customer…" />
          </div>
          <div className="flex items-center gap-3 ml-auto">
          {/* Who to ring. Renders nothing when no active CS is assigned — see SupportContactCard. */}
          <div className="hidden lg:block pr-3 border-r border-slate-200"><SupportContactCard compact /></div>
          {/* Total wallet amount + remittance / freight / recharge breakdown on hover */}
          {/* Balance widget is a payment surface too — hidden for App Store accounts (1.2.1). */}
          {!isShopifyFreeApp && <WalletBalanceMenu portal="merchant" />}
          <a href="/track" target="_blank" rel="noreferrer" title="Public tracking"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 text-xs font-medium transition">
            <Truck size={14} /> Track
          </a>
          <a href={`${API_BASE_URL}/download/apk/merchant`} target="_blank" rel="noreferrer" title="Download Merchant Android App"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-emerald-700 text-xs font-medium transition">
            <Smartphone size={14} /> Download App
          </a>
          <AccountSwitcher />
          <button className="text-slate-400 hover:text-slate-600 relative">
            <Bell size={18} />
          </button>
          <MerchantUserMenu displayName={displayName} user={user} onLogout={() => logout()} onNavigate={navigate} hideWallet={isShopifyFreeApp} />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          {/* Platform-wide notices from Settings > Merchant Notifications. Inside <main> rather than
              pinned above it so it scrolls away instead of permanently eating vertical space. */}
          <MerchantNoticeBar />
          <Outlet />
        </main>
      </div>
      <ChatbotWidget />
    </div>
  );
}

function MerchantUserMenu({ displayName, user, onLogout, onNavigate, hideWallet }: {
  displayName?: string | null;
  user: any;
  onLogout: () => void;
  onNavigate: (p: string) => void;
  /** App Store accounts see no payment surface anywhere — see filterMerchantNav (1.2.1). */
  hideWallet?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as any)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100">
        <div className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
          {(displayName ?? '?').charAt(0).toUpperCase()}
        </div>
        <div className="text-left hidden md:block">
          <div className="text-sm font-medium text-slate-900">{displayName}</div>
          <div className="text-xs text-slate-500">Merchant</div>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition hidden md:block ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-lg shadow-xl py-2 z-50">
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900 truncate">{displayName}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">Theme</span>
            <ThemeToggle />
          </div>
          <button onClick={() => { setOpen(false); onNavigate('/settings'); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
            <Settings size={14} /> My Profile &amp; Settings
          </button>
          {!hideWallet && (
            <button onClick={() => { setOpen(false); onNavigate('/wallet'); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
              <Wallet size={14} /> Wallet
            </button>
          )}
          <div className="border-t border-slate-100 mt-1 pt-1">
            <button onClick={() => { setOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
